import { Box, Button, List, ListItem, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import PrivacyCheckboxes from "../../../components/PrivacyCheckboxes";
import { updateFields } from "../../../redux/profileSlice";
import {
  DEMOGRAPHIC_ANSWER_FIELDS_BY_VISIBILITY,
  formatCommunityAnswer,
  formatDisabilityAnswer,
  formatEthnicity,
  formatGenderIdentity,
} from "../../../constants/demographicOptions";

const EDIT_BUTTON_STYLE = {
  border: "1px solid #AAB6C8",
  padding: "8px 20px",
  borderRadius: "24px",
  textTransform: "none",
  color: "#152134",
  flexShrink: 0,
};

const answer = (value) => value || "Skipped";

const V2ProfileDemographics = ({ localProfile }) => {
  const profile = localProfile?.profile ?? {};
  const demographics = localProfile?.demographics ?? {};
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const edit = (path) =>
    navigate(path, {
      state: { localProfile },
    });

  const hasAnswer = (field) => {
    const answerField = DEMOGRAPHIC_ANSWER_FIELDS_BY_VISIBILITY[field];

    return (demographics[answerField] ?? []).length > 0;
  };

  const setVisibility = (field, value) => {
    if (field === "locationVisibility") {
      dispatch(updateFields({ field: "locationVisibility", value }));
      return;
    }
    if (!hasAnswer(field)) return;
    dispatch(updateFields({ field: `demographics.${field}`, value }));
  };

  const gender = (demographics.genderIdentities ?? [])
    .map((value) => formatGenderIdentity(value, demographics.customGender))
    .join(", ");
  const ethnicity = (demographics.ethnicities ?? [])
    .map((value) => formatEthnicity(value, demographics.customEthnicity))
    .join(", ");
  const languages = (profile.languagesSpoken ?? [])
    .map((value) =>
      value === "Other" ? profile.customLanguages?.trim() || value : value,
    )
    .filter(Boolean)
    .join(", ");

  const rows = [
    ["Gender", answer(gender), "/profile/edit-gender-identity"],
    ["Ethnicity", answer(ethnicity), "/profile/edit-ethnicity"],
    [
      "2SLGBTQ+",
      formatCommunityAnswer(demographics.lgbtqIdentities?.[0]),
      "/profile/edit-lgbtq-details",
    ],
    [
      "Disability",
      formatDisabilityAnswer(demographics.disabilityStatuses?.[0]),
      "/profile/edit-disability-status",
    ],
    ["Languages", answer(languages), "/profile/edit-languages-spoken"],
  ];

  return (
    <Box sx={{ mt: 3 }}>
      <Typography sx={{ fontWeight: 600, mb: 1 }}>Demographics</Typography>
      {rows.map(([label, value, path]) => (
        <Box
          key={label}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 1.5,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: "#152134" }}>{label}</Typography>
            <Typography sx={{ color: "#2A4876", wordBreak: "break-word" }}>
              {value}
            </Typography>
          </Box>
          <Button
            style={EDIT_BUTTON_STYLE}
            onClick={() => edit(path)}
            aria-label={`Edit ${label}`}
          >
            Edit
          </Button>
        </Box>
      ))}

      <Typography sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Privacy</Typography>
      <List disablePadding>
        <ListItem disableGutters>
          <PrivacyCheckboxes
            label="Display my location on my profile"
            checked={profile.locationVisibility}
            onChange={(event) =>
              setVisibility("locationVisibility", event.target.checked)
            }
          />
        </ListItem>
        <ListItem disableGutters>
          <PrivacyCheckboxes
            label="Display my race/ethnicity on my profile"
            checked={
              hasAnswer("ethnicityVisible") && demographics.ethnicityVisible
            }
            disabled={!hasAnswer("ethnicityVisible")}
            onChange={(event) =>
              setVisibility("ethnicityVisible", event.target.checked)
            }
          />
        </ListItem>
        <ListItem disableGutters>
          <PrivacyCheckboxes
            label="Display my gender on my profile"
            checked={hasAnswer("genderVisible") && demographics.genderVisible}
            disabled={!hasAnswer("genderVisible")}
            onChange={(event) =>
              setVisibility("genderVisible", event.target.checked)
            }
          />
        </ListItem>
        <ListItem disableGutters>
          <PrivacyCheckboxes
            label="Display my 2SLGBTQ+ identification on my profile"
            checked={hasAnswer("lgbtqVisible") && demographics.lgbtqVisible}
            disabled={!hasAnswer("lgbtqVisible")}
            onChange={(event) =>
              setVisibility("lgbtqVisible", event.target.checked)
            }
          />
        </ListItem>
        <ListItem disableGutters>
          <PrivacyCheckboxes
            label="Display my disability status on my profile"
            checked={
              hasAnswer("disabilityVisible") && demographics.disabilityVisible
            }
            disabled={!hasAnswer("disabilityVisible")}
            onChange={(event) =>
              setVisibility("disabilityVisible", event.target.checked)
            }
          />
        </ListItem>
      </List>
    </Box>
  );
};

export default V2ProfileDemographics;
